/**
 * Copyright ©2022 Drivepoint
 */

import React, {Suspense, useEffect, useState} from "react";
import {ThemeProvider} from "@mui/material";
import {SnackbarProvider} from "notistack";
import {useLocation, useNavigate} from "react-router-dom";
import {Config, Logger, useLocalStorage} from "@bainbridge-growth/node-frontend";
import {LoggerLevelFromString} from "@bainbridge-growth/node-frontend/src/logger/LoggerLevel";
import DPNotifications from "@components/DPNotifications/DPNotifications";
import DPHeader from "@components/layout/DPHeader/DPHeader";
import usePage from "@hooks/usePage";
import useStateChange from "@hooks/useStateChange";
import useThemeContext from "@hooks/useThemeContext";
import {FirebaseAuthenticationState} from "@services/firebase/Firebase";
import Reload from "@services/Reload";
import DrivepointUser from "@services/user/DrivepointUser";
import Suspended from "../../widgets/component/Suspended";
import DPErrorBoundary from "../DPErrorBoundary";
import NavigationDrawer from "../navigation/NavigationDrawer/NavigationDrawer";
import Routing from "../Routing/Routing";
import "./App.less";

export default function App(): any {

  const defaultLoggerLevel = LoggerLevelFromString(Config.get("logger.level"));

  const {themeProvider, ThemeContext, theme} = useThemeContext();
  const page = usePage();
  const user = useStateChange<DrivepointUser>("user");
  const authentication = useStateChange<FirebaseAuthenticationState>("authentication");
  const navigate = useNavigate();
  const location = useLocation();
  const [loggerLevel] = useLocalStorage<number>(`${Config.get("environment")}.logger.level`, defaultLoggerLevel);

  const [navigationOpen, setNavigationOpen] = useState<boolean>(getNavigationDrawerState());

  useEffect(() => {
    Reload.handleReloadReason();
  }, []);

  useEffect(() => {
    window.Intercom?.("update");
  }, [location.pathname]);

  useEffect(() => {
    if (loggerLevel != null) { Logger.level = Logger.level = loggerLevel; }
  }, [loggerLevel]);

  useEffect(() => {
    setNavigationDrawerState();
  }, [navigationOpen]);

  function getNavigationDrawerState(): boolean {
    const state = localStorage.getItem("navigationDrawerState");
    return state != null ? JSON.parse(state) : true;
  }

  function setNavigationDrawerState(): void {
    localStorage.setItem("navigationDrawerState", JSON.stringify(navigationOpen));
  }

  function toggleNavigationDrawer(): void {
    setNavigationOpen(!navigationOpen);
  }

  function renderNavigationDrawer(): any {
    if (page.current?.hideChrome) { return; }
    return <NavigationDrawer open={navigationOpen} toggle={toggleNavigationDrawer} resizable />;
  }

  if (authentication === FirebaseAuthenticationState.AUTHENTICATED && !user?.companies?.length && !page.current?.public) {
    navigate("/quickstart", {replace: true});
    return;
  }

  return <DPErrorBoundary fallback={<p>Here will be error component</p>}>
    <ThemeContext.Provider value={themeProvider}>
      <ThemeProvider theme={theme}>
        <main id="main" className={`app desktop${page.current?.hideChrome ? " app-responsive" : ""}`}>
          {renderNavigationDrawer()}
          <section className="app-content">
            <DPHeader />
            <Suspense fallback={<Suspended />}>
              <SnackbarProvider preventDuplicate maxSnack={10}>
                <Routing />
              </SnackbarProvider>
            </Suspense>
          </section>
        </main>
        <DPNotifications />
      </ThemeProvider>
    </ThemeContext.Provider>
  </DPErrorBoundary>;

}
